'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
// next/navigation hooks removed for hydration safety - using window APIs instead
import { calculateROSI } from '@/lib/calculators/roi-calculator';
import { SCENARIO_TEMPLATES } from '@/lib/calculators/roi-constants';
import { SecurityInvestment, RiskProfile, ROSIResult, ComplianceFramework } from '@/types/calculator';
import { SelectInput, MultiSelect, SliderInput, TextInput, DualInputWithSlider } from './FormInputs';
import { CalculatorCard, CalculatorSection } from './CalculatorCard';
import { MetricCard, BreakdownTable, RecommendationBadge } from './ResultsDisplay';
import { LineChart, BarChart } from './Charts';
import { decodeURLParams, importBreachCostToROI, importBudgetToROI } from '@/lib/calculators/calculator-integration';
import { Toast } from './Toast';
import { generateROIPDF } from '@/lib/calculators/roi-pdf';
import { SecurityAwarenessTrainingTab } from './SecurityAwarenessTrainingTab';
import { LeadCaptureForm } from './LeadCaptureForm';

type ActiveTab = 'general' | 'training';

const tabs: { id: ActiveTab; label: string; icon: string }[] = [
    { id: 'general', label: 'General ROI', icon: '📊' },
    { id: 'training', label: 'Security Training ROI', icon: '🎓' },
];
const scenarioOptions = [
    { value: 'mdr_soc', label: '24/7 SOC/MDR Service' },
    { value: 'mfa', label: 'Multi-Factor Authentication' },
    { value: 'edr', label: 'EDR Platform' },
    { value: 'vciso', label: 'Virtual CISO Service' },
    { value: 'security_awareness', label: 'Security Awareness Training' },
    { value: 'backup_dr', label: 'Backup & Disaster Recovery' },
    { value: 'vuln_mgmt', label: 'Vulnerability Management' },
    { value: 'custom', label: 'Custom Investment' }
];
const maturityOptions = [
    { value: 'minimal', label: 'Minimal' },
    { value: 'developing', label: 'Developing' },
    { value: 'defined', label: 'Defined' },
    { value: 'managed', label: 'Managed' },
    { value: 'optimizing', label: 'Optimizing' }
];
const complianceOptions: {
    value: ComplianceFramework;
    label: string;
}[] = [
    { value: 'hipaa', label: 'HIPAA' },
    { value: 'pci_dss', label: 'PCI DSS' },
    { value: 'gdpr', label: 'GDPR' },
    { value: 'soc2', label: 'SOC 2' },
    { value: 'iso27001', label: 'ISO 27001' },
    { value: 'nist_csf', label: 'NIST CSF' },
    { value: 'cmmc', label: 'CMMC' },
    { value: 'glba', label: 'GLBA' },
    { value: 'state_privacy', label: 'State Privacy Laws' }
];
const industryOptions = [
    { value: 'general', label: 'General / Not Listed' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'technology', label: 'Technology' },
    { value: 'retail', label: 'Retail / E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'professional_services', label: 'Professional Services' },
    { value: 'education', label: 'Education' },
    { value: 'energy', label: 'Energy & Utilities' },
    { value: 'hospitality', label: 'Hospitality & Travel' },
    { value: 'government', label: 'Government / Public Sector' }
];
// Industry-specific average breach costs (based on IBM Cost of Breach Report 2024)
const INDUSTRY_BREACH_COSTS: Record<string, number> = {
    healthcare: 10930000, // Healthcare: $10.93M
    financial: 6080000, // Financial: $6.08M
    technology: 5170000, // Technology: $5.17M
    energy: 5040000, // Energy: $5.04M
    pharmaceutical: 5060000, // Pharmaceuticals: $5.06M
    education: 4470000, // Education: $4.47M
    manufacturing: 4560000, // Manufacturing: $4.56M
    hospitality: 3380000, // Hospitality: $3.38M
    retail: 3480000, // Retail: $3.48M
    professional_services: 4880000, // Services: $4.88M
    government: 2070000, // Public: $2.07M
    general: 4880000 // Average: $4.88M
};
export function ROICalculatorClient() {
    // Use window.location.search instead of useSearchParams for hydration safety
    const getSearchParams = useCallback(() => {
        if (typeof window === 'undefined') return new URLSearchParams();
        return new URLSearchParams(window.location.search);
    }, []);

    const searchParams = useMemo(() => getSearchParams(), [getSearchParams]);

    // Replace router.replace with window.history for hydration safety
    const updateURL = useCallback((params: URLSearchParams) => {
        if (typeof window === 'undefined') return;
        const newURL = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(window.history.state, '', newURL);
    }, []);

    const [activeTab, setActiveTab] = useState<ActiveTab>('general');
    const [step, setStep] = useState(1);
    const [showResults, setShowResults] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    // Investment selection
    const [selectedScenario, setSelectedScenario] = useState('mdr_soc');
    const [investmentName, setInvestmentName] = useState('24/7 SOC/MDR Service');
    const [implementationCost, setImplementationCost] = useState(25000);
    const [annualCost, setAnnualCost] = useState(150000);
    const [riskMitigation, setRiskMitigation] = useState(45);
    // Risk profile
    const [industry, setIndustry] = useState('general');
    const [annualRevenue, setAnnualRevenue] = useState(25000000);
    const [breachProbability, setBreachProbability] = useState(30);
    const [avgBreachCost, setAvgBreachCost] = useState(4880000);
    const [breachCostSource, setBreachCostSource] = useState<'industry' | 'calculator' | 'manual'>('industry');
    const [currentMaturity, setCurrentMaturity] = useState<'minimal' | 'developing' | 'defined' | 'managed' | 'optimizing'>('developing');
    // Additional benefits
    const [complianceFrameworks, setComplianceFrameworks] = useState<ComplianceFramework[]>([]);
    const [productivitySavings, setProductivitySavings] = useState(0);
    const [insuranceReduction, setInsuranceReduction] = useState(0);
    const [result, setResult] = useState<ROSIResult | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [shouldAutoCalculate, setShouldAutoCalculate] = useState(false);
    // Load URL parameters on mount
    useEffect(() => {
        if (searchParams && !isInitialized) {
            const params = decodeURLParams(searchParams);
            let hasCompleteParams = false;
            // Check if coming from Breach Cost Calculator
            if (params.source === 'breach_cost') {
                const importedData = importBreachCostToROI(params);
                if (importedData) {
                    if (importedData.breachCost) {
                        setAvgBreachCost(importedData.breachCost);
                        setBreachCostSource('calculator');
                    }
                    if (importedData.employeeCount) {
                        const estimatedRevenue = importedData.employeeCount * 250000;
                        setAnnualRevenue(estimatedRevenue);
                    }
                }
            }
            // Check if coming from Budget Calculator
            if (params.source === 'budget') {
                const importedData = importBudgetToROI(params);
                if (importedData) {
                    if (importedData.annualRevenue)
                        setAnnualRevenue(importedData.annualRevenue);
                }
            }
            // Load all URL parameters
            if (params.scenario)
                setSelectedScenario(params.scenario);
            if (params.investmentName)
                setInvestmentName(params.investmentName);
            if (params.implementationCost)
                setImplementationCost(parseFloat(params.implementationCost));
            if (params.annualCost)
                setAnnualCost(parseFloat(params.annualCost));
            if (params.riskMitigation)
                setRiskMitigation(parseFloat(params.riskMitigation));
            if (params.industry)
                setIndustry(params.industry);
            if (params.annualRevenue)
                setAnnualRevenue(parseFloat(params.annualRevenue));
            if (params.breachProbability)
                setBreachProbability(parseFloat(params.breachProbability));
            if (params.avgBreachCost) {
                setAvgBreachCost(parseFloat(params.avgBreachCost));
                setBreachCostSource('manual');
            }
            if (params.currentMaturity)
                setCurrentMaturity(params.currentMaturity as 'minimal' | 'developing' | 'defined' | 'managed' | 'optimizing');
            if (params.productivitySavings)
                setProductivitySavings(parseFloat(params.productivitySavings));
            if (params.insuranceReduction)
                setInsuranceReduction(parseFloat(params.insuranceReduction));
            if (params.complianceFrameworks) {
                const frameworks = params.complianceFrameworks.split(',').filter(f => f.trim()) as ComplianceFramework[];
                setComplianceFrameworks(frameworks);
            }
            // Check for tab parameter
            if (params.tab === 'training') {
                setActiveTab('training');
            }

            // Check if we have all required parameters to auto-calculate
            hasCompleteParams = !!(params.scenario &&
                params.investmentName &&
                params.implementationCost &&
                params.annualCost &&
                params.industry &&
                params.annualRevenue &&
                params.breachProbability &&
                params.avgBreachCost &&
                params.currentMaturity);
            setIsInitialized(true);
            // Flag to auto-calculate results if we have complete params (from a shared link)
            if (hasCompleteParams) {
                setShouldAutoCalculate(true);
            }
        }
    }, [searchParams, isInitialized]);
    // Auto-update breach cost when industry changes (only if not from calculator)
    useEffect(() => {
        if (isInitialized && breachCostSource === 'industry') {
            const industryCost = INDUSTRY_BREACH_COSTS[industry] || INDUSTRY_BREACH_COSTS.general;
            setAvgBreachCost(industryCost);
        }
    }, [industry, isInitialized, breachCostSource]);
    // Update URL whenever inputs change
    useEffect(() => {
        if (!isInitialized)
            return;
        const params = new URLSearchParams();
        params.set('scenario', selectedScenario);
        params.set('investmentName', investmentName);
        params.set('implementationCost', String(implementationCost));
        params.set('annualCost', String(annualCost));
        params.set('riskMitigation', String(riskMitigation));
        params.set('industry', industry);
        params.set('annualRevenue', String(annualRevenue));
        params.set('breachProbability', String(breachProbability));
        params.set('avgBreachCost', String(avgBreachCost));
        params.set('currentMaturity', currentMaturity);
        params.set('productivitySavings', String(productivitySavings));
        params.set('insuranceReduction', String(insuranceReduction));
        if (complianceFrameworks.length > 0) {
            params.set('complianceFrameworks', complianceFrameworks.join(','));
        }
        if (activeTab !== 'general') {
            params.set('tab', activeTab);
        }
        // Update URL without page reload (using window.history for hydration safety)
        updateURL(params);
    }, [
        isInitialized,
        selectedScenario,
        investmentName,
        implementationCost,
        annualCost,
        riskMitigation,
        industry,
        annualRevenue,
        breachProbability,
        avgBreachCost,
        currentMaturity,
        productivitySavings,
        insuranceReduction,
        complianceFrameworks,
        activeTab,
        updateURL
    ]);
    const handleScenarioChange = (scenarioKey: string) => {
        setSelectedScenario(scenarioKey);
        if (scenarioKey !== 'custom' && SCENARIO_TEMPLATES[scenarioKey]) {
            const template = SCENARIO_TEMPLATES[scenarioKey];
            setInvestmentName(template.name || '');
            setImplementationCost(template.implementationCost || 0);
            setAnnualCost(template.annualRecurringCost || 0);
            setRiskMitigation(template.riskMitigationPercent || 0);
        }
    };
    const calculateResults = useCallback(() => {
        // Map 5-level maturity to 4-level SecurityMaturityLevel
        const mapMaturityLevel = (level: 'minimal' | 'developing' | 'defined' | 'managed' | 'optimizing'): 'minimal' | 'basic' | 'moderate' | 'advanced' => {
            const mapping = {
                minimal: 'minimal' as const,
                developing: 'basic' as const,
                defined: 'moderate' as const,
                managed: 'advanced' as const,
                optimizing: 'advanced' as const
            };
            return mapping[level];
        };
        const investment: SecurityInvestment = {
            id: `inv-${Date.now()}`,
            name: investmentName,
            category: 'detection_monitoring',
            implementationCost,
            annualRecurringCost: annualCost,
            personnelHours: 40,
            riskMitigationPercent: riskMitigation,
            timeToEffectiveness: 'short',
            confidenceLevel: 'medium',
            threatsAddressed: ['phishing', 'malware', 'ransomware'],
            complianceBenefits: complianceFrameworks,
            productivitySavings,
            insurancePremiumReduction: insuranceReduction
        };
        const riskProfile: RiskProfile = {
            ALE: avgBreachCost * (breachProbability / 100),
            industryAverageBreach: INDUSTRY_BREACH_COSTS[industry] || INDUSTRY_BREACH_COSTS.general,
            currentMaturityLevel: mapMaturityLevel(currentMaturity)
        };
        const calculation = calculateROSI(investment, riskProfile);
        setResult(calculation);
        setShowResults(true);
    }, [investmentName, implementationCost, annualCost, riskMitigation, complianceFrameworks, productivitySavings, insuranceReduction, avgBreachCost, breachProbability, industry, currentMaturity]);
    // Auto-calculate results after URL params are loaded
    useEffect(() => {
        if (isInitialized && shouldAutoCalculate && !result) {
            // Small delay to ensure all state updates are complete
            const timer = setTimeout(() => {
                calculateResults();
                setShouldAutoCalculate(false);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isInitialized, shouldAutoCalculate, result, calculateResults]);
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };
    const formatPercentage = (value: number) => {
        return `${value.toFixed(1)}%`;
    };
    const formatYears = (value: number | null) => {
        if (value === null)
            return 'Never';
        if (value < 1)
            return `${(value * 12).toFixed(1)} months`;
        return `${value.toFixed(1)} years`;
    };
    const renderExecutiveSummary = (markdown: string) => {
        const lines = markdown.split('\n');
        const elements: React.ReactElement[] = [];
        lines.forEach((line, index) => {
            const trimmed = line.trim();
            if (!trimmed)
                return;
            // H2 headings (##)
            if (trimmed.startsWith('## ')) {
                elements.push(<h2 key={index} className="text-3xl font-bold text-gray-900 mt-8 mb-6 first:mt-0">
            {trimmed.replace('## ', '')}
          </h2>);
            }
            // H3 headings (###)
            else if (trimmed.startsWith('### ')) {
                elements.push(<h3 key={index} className="text-xl font-bold text-blue-600 mt-6 mb-3 flex items-center gap-2">
            <span className="text-2xl">📊</span>
            {trimmed.replace('### ', '')}
          </h3>);
            }
            // List items (-)
            else if (trimmed.startsWith('- ')) {
                const text = trimmed.replace('- ', '');
                // Handle bold text with **
                const parts = text.split(/(\*\*.*?\*\*)/g);
                elements.push(<div key={index} className="flex items-start gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-blue-500 mt-1">●</span>
            <p className="text-gray-700 flex-1">
              {parts.map((part, i) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={i} className="text-gray-900 font-bold">{part.slice(2, -2)}</strong>;
                        }
                        return part;
                    })}
            </p>
          </div>);
            }
            // Regular paragraphs
            else {
                // Handle bold text with **
                const parts = trimmed.split(/(\*\*.*?\*\*)/g);
                elements.push(<p key={index} className="text-gray-700 mb-4 leading-relaxed text-lg">
            {parts.map((part, i) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={i} className="text-gray-900 font-bold">{part.slice(2, -2)}</strong>;
                        }
                        return part;
                    })}
          </p>);
            }
        });
        return <div className="space-y-2">{elements}</div>;
    };
    const renderActionButtons = () => (<div className="flex flex-col sm:flex-row gap-4 justify-center">
      <button onClick={() => {
            generateROIPDF({
                result: result!,
                inputs: {
                    scenario: selectedScenario,
                    investmentName,
                    implementationCost,
                    annualCost,
                    riskMitigation,
                    industry,
                    annualRevenue,
                    breachProbability,
                    avgBreachCost,
                    currentMaturity,
                    productivitySavings,
                    insuranceReduction,
                    complianceFrameworks
                }
            });
        }} className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
        <span>📄</span>
        <span>Download PDF Report</span>
      </button>
      <button onClick={() => {
            const currentURL = window.location.href;
            navigator.clipboard.writeText(currentURL);
            setShowToast(true);
        }} className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
        <span>🔗</span>
        <span>Copy Share Link</span>
      </button>
      <button onClick={() => setShowResults(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors shadow hover:shadow-lg flex items-center justify-center gap-2">
        <span>↻</span>
        <span>Start Over</span>
      </button>
    </div>);
    return (<div className="max-w-7xl mx-auto">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Calculator tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id !== activeTab) {
                  setShowResults(false);
                  setStep(1);
                }
              }}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'training' ? (
        <SecurityAwarenessTrainingTab />
      ) : !showResults ? (<>
          {/* Step Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between max-w-xl mx-auto">
              {[1, 2, 3].map((s) => (<React.Fragment key={s}>
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-bold ${s === step
                    ? 'bg-green-500 text-white border-green-500'
                    : s < step
                        ? 'bg-green-500 text-white border-green-500'
                        : 'bg-white text-gray-400 border-gray-300'}`}>
                    {s < step ? '✓' : s}
                  </div>
                  {s < 3 && (<div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-green-500' : 'bg-gray-300'}`}/>)}
                </React.Fragment>))}
            </div>
            <div className="flex justify-between max-w-xl mx-auto mt-2 text-xs font-semibold text-gray-600">
              <span>Investment</span>
              <span>Risk Profile</span>
              <span>Benefits</span>
            </div>
          </div>

          {/* Step 1: Investment Selection */}
          {step === 1 && (<CalculatorCard>
              <CalculatorSection title="Security Investment" description="Select a pre-built scenario or create custom investment" icon="💰">
                <SelectInput label="Investment Type" value={selectedScenario} onChange={handleScenarioChange} options={scenarioOptions} helpText="Select a pre-built scenario or choose 'Custom' to enter your own" required/>

                {selectedScenario === 'custom' && (<div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800 mb-4">
                      <strong>Custom Investment Mode:</strong> Enter your own investment details
                      below.
                    </p>
                  </div>)}

                <TextInput label="Investment Name" value={investmentName} onChange={setInvestmentName} helpText="Name of the security investment" required/>

                <DualInputWithSlider label="One-Time Implementation Cost" value={implementationCost} onChange={setImplementationCost} min={1000} max={10000000} prefix="$" helpText="Setup, deployment, training costs" required/>

                <DualInputWithSlider label="Annual Recurring Cost" value={annualCost} onChange={setAnnualCost} min={1000} max={10000000} prefix="$" helpText="Licensing, maintenance, ongoing costs" required/>

                <SliderInput label="Risk Mitigation Percentage" value={riskMitigation} onChange={setRiskMitigation} min={0} max={100} unit="%" helpText="How much does this investment reduce your risk?"/>
              </CalculatorSection>

              <div className="flex justify-end mt-6">
                <button onClick={() => setStep(2)} className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                  Next: Risk Profile
                </button>
              </div>
            </CalculatorCard>)}

          {/* Step 2: Risk Profile */}
          {step === 2 && (<CalculatorCard>
              <CalculatorSection title="Risk Profile" description="Your organization's current risk exposure" icon="⚠️">
                <SelectInput label="Industry" value={industry} onChange={(value) => {
                    setIndustry(value);
                    if (breachCostSource === 'industry') {
                        const industryCost = INDUSTRY_BREACH_COSTS[value] || INDUSTRY_BREACH_COSTS.general;
                        setAvgBreachCost(industryCost);
                    }
                }} options={industryOptions} helpText="Your industry affects average breach costs" required/>

                <DualInputWithSlider label="Annual Revenue" value={annualRevenue} onChange={setAnnualRevenue} min={100000} max={10000000000} prefix="$" helpText="Used to calculate risk as % of revenue" required/>

                <SliderInput label="Breach Probability" value={breachProbability} onChange={setBreachProbability} min={0} max={100} unit="%" helpText="Likelihood of a breach occurring this year"/>

                {breachCostSource === 'calculator' && (<div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">✓</div>
                      <div>
                        <h4 className="font-bold text-green-900 mb-1" id="breach-cost-imported">
                          Breach Cost Imported
                        </h4>
                        <p className="text-sm text-green-800">
                          Using your calculated breach cost from the Data Breach Cost Calculator.
                        </p>
                      </div>
                    </div>
                  </div>)}

                <DualInputWithSlider label="Average Breach Cost" value={avgBreachCost} onChange={(value) => {
                    setAvgBreachCost(value);
                    setBreachCostSource('manual');
                }} min={100000} max={50000000} prefix="$" helpText={breachCostSource === 'industry' ? `Industry average: ${formatCurrency(INDUSTRY_BREACH_COSTS[industry] || INDUSTRY_BREACH_COSTS.general)}` : 'Estimated cost if a breach occurs'} required/>

                {breachCostSource === 'industry' && (<div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">💡</div>
                      <div>
                        <h4 className="font-bold text-blue-900 mb-1" id="need-a-more-accurate-estimate">
                          Need a More Accurate Estimate?
                        </h4>
                        <p className="text-sm text-blue-800 mb-3">
                          We've populated the industry average breach cost for {industryOptions.find(i => i.value === industry)?.label}.
                          For a personalized estimate based on your company size, data sensitivity, and security posture, use our Data Breach Cost Calculator.
                        </p>
                        <a href="/tools/planning/data-breach-cost-calculator" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 underline">
                          Calculate Your Breach Cost
                          <span>→</span>
                        </a>
                      </div>
                    </div>
                  </div>)}

                <div className="p-4 bg-orange-50 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">📊</div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1" id="annual-loss-expectancy-ale">
                        Annual Loss Expectancy (ALE)
                      </h4>
                      <p className="text-sm text-gray-700">
                        {formatCurrency(avgBreachCost * (breachProbability / 100))} per year
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        ALE = Breach Cost × Probability
                      </p>
                    </div>
                  </div>
                </div>

                <SelectInput label="Current Security Maturity" value={currentMaturity} onChange={(v) => setCurrentMaturity(v as 'minimal' | 'developing' | 'defined' | 'managed' | 'optimizing')} options={maturityOptions} helpText="Your current security program maturity level" required/>
              </CalculatorSection>

              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(1)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors">
                  Back
                </button>
                <button onClick={() => setStep(3)} className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                  Next: Additional Benefits
                </button>
              </div>
            </CalculatorCard>)}

          {/* Step 3: Additional Benefits */}
          {step === 3 && (<CalculatorCard>
              <CalculatorSection title="Additional Benefits" description="Beyond risk reduction, what else does this investment provide?" icon="✨">
                <MultiSelect label="Compliance Benefits" value={complianceFrameworks} onChange={(values) => setComplianceFrameworks(values as ComplianceFramework[])} options={complianceOptions.map((opt) => ({
                    value: opt.value,
                    label: opt.label
                }))} helpText="Which compliance frameworks does this help you achieve?"/>

                <DualInputWithSlider label="Annual Productivity Savings" value={productivitySavings} onChange={setProductivitySavings} min={0} max={1000000} prefix="$" helpText="Time saved by automation or efficiency improvements"/>

                <SliderInput label="Insurance Premium Reduction" value={insuranceReduction} onChange={setInsuranceReduction} min={0} max={50} unit="%" helpText="Expected reduction in cyber insurance premiums"/>
              </CalculatorSection>

              <div className="flex justify-between mt-6">
                <button onClick={() => setStep(2)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors">
                  Back
                </button>
                <button onClick={calculateResults} className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                  Calculate ROI
                </button>
              </div>
            </CalculatorCard>)}
        </>) : (<>
          {/* Results Display */}
          {result && (<>
              <div className="mb-8">
                <button onClick={() => setShowResults(false)} className="text-green-500 hover:text-green-600 font-semibold flex items-center gap-2">
                  ← Recalculate
                </button>
              </div>

              {/* Recommendation Badge */}
              <div className="flex items-center justify-center mb-8">
                <RecommendationBadge recommendation={result.recommendation}/>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <MetricCard label="Year 1 ROI" value={formatPercentage(result.rosiYear1)} description="% return on your investment in the first year" icon="📈" color={result.rosiYear1 > 50 ? 'green' : result.rosiYear1 > 0 ? 'blue' : 'red'}/>
                <MetricCard label="5-Year ROI" value={formatPercentage(result.rosi5Year)} description="Total return over 5 years" icon="💹" color="green"/>
                <MetricCard label="Payback Period" value={formatYears(result.paybackPeriod)} description="How long until benefits exceed costs" icon="⏱️" color="blue"/>
                <MetricCard label="5-Year NPV" value={formatCurrency(result.npv5Year)} description="Total value created after 5 years" icon="💰" color={result.npv5Year > 0 ? 'green' : 'red'}/>
              </div>

              {/* Action Buttons - Top */}
              <div className="mb-8">
                {renderActionButtons()}
              </div>

              {/* Executive Summary */}
              <CalculatorCard className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <span className="text-3xl">📋</span>
                  Executive Summary
                </h2>
                {renderExecutiveSummary(result.executiveSummary)}
              </CalculatorCard>

              {/* Risk Mitigation Breakdown */}
              <CalculatorCard className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <span className="text-3xl">🛡️</span>
                  Risk Mitigation Analysis
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
                    <div className="text-sm font-semibold text-gray-600 mb-2">Current Risk (ALE)</div>
                    <div className="text-3xl font-bold text-red-600 mb-3">
                      {formatCurrency(avgBreachCost * (breachProbability / 100))}/year
                    </div>
                    <p className="text-sm text-gray-600">
                      Your Annual Loss Expectancy — the expected financial impact from cyber incidents based on breach probability and average breach cost.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
                    <div className="text-sm font-semibold text-gray-600 mb-2">Risk Reduction</div>
                    <div className="text-3xl font-bold text-blue-600 mb-3">
                      {formatPercentage(riskMitigation)}
                    </div>
                    <p className="text-sm text-gray-600">
                      Percentage of current risk that this security investment is expected to mitigate through prevention and detection.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 border-2 border-green-200">
                    <div className="text-sm font-semibold text-gray-600 mb-2">Annual Risk Savings</div>
                    <div className="text-3xl font-bold text-green-600 mb-3">
                      {formatCurrency(result.benefits.riskReductionAnnual)}
                    </div>
                    <p className="text-sm text-gray-600">
                      Expected yearly reduction in losses from prevented or mitigated security incidents after this investment.
                    </p>
                  </div>
                </div>
              </CalculatorCard>

              {/* Cost vs Benefits */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <BreakdownTable title="Investment Costs" items={[
                    {
                        label: 'One-Time Implementation',
                        value: formatCurrency(result.costs.oneTime),
                        tooltip: 'Initial setup, deployment, configuration, and training costs for the security investment.'
                    },
                    {
                        label: 'Annual Recurring',
                        value: formatCurrency(result.costs.annual),
                        tooltip: 'Yearly licensing fees, maintenance, support, and subscription costs for the security solution.'
                    },
                    {
                        label: 'Personnel Time',
                        value: formatCurrency(result.costs.personnelCost),
                        tooltip: 'Internal staff time required for implementation, management, and ongoing operations. Calculated based on personnel hours needed.'
                    }
                ]} total={{
                    label: 'Year 1 Total Cost',
                    value: formatCurrency(result.costs.year1Total)
                }}/>

                <BreakdownTable title="Annual Benefits" items={[
                    {
                        label: 'Risk Reduction',
                        value: formatCurrency(result.benefits.riskReductionAnnual),
                        tooltip: 'Expected reduction in Annual Loss Expectancy (ALE) from reduced breach likelihood and impact. Based on risk mitigation percentage applied to your current risk profile.'
                    },
                    {
                        label: 'Additional Benefits',
                        value: formatCurrency(result.benefits.additionalBenefits),
                        tooltip: 'Productivity savings from automation, insurance premium reductions, avoided audit costs, and compliance benefits beyond risk reduction.'
                    }
                ]} total={{
                    label: 'Total Annual Benefit',
                    value: formatCurrency(result.benefits.totalBenefitYear1)
                }}/>
              </div>

              {/* Multi-Year Projection */}
              <CalculatorCard className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="text-2xl">📈</span>
                  5-Year Financial Projection
                </h3>
                <p className="text-gray-600 mb-6">
                  This chart shows your cumulative Net Present Value (NPV) over 5 years — the total financial
                  value created by this investment after accounting for costs and the time value of money.
                  The line trending upward indicates growing value as annual benefits compound over time.
                </p>
                <LineChart title="Cumulative Net Present Value Over Time" data={result.multiYearProjection.map((p) => ({
                    x: `Year ${p.year}`,
                    y: p.npv
                }))} xLabel="Year" yLabel="Net Present Value ($)" yUnit="$" height={400}/>
              </CalculatorCard>

              {/* Sensitivity Analysis */}
              <CalculatorCard className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-2xl">🎯</span>
                  Sensitivity Analysis
                </h3>
                <p className="text-gray-600 mb-6">
                  Security investments rarely perform exactly as planned. This shows your ROI under different
                  scenarios: <strong>Pessimistic</strong> (investment underperforms), <strong>Conservative</strong> (modest
                  results), <strong>Expected</strong> (your current assumptions), and <strong>Optimistic</strong> (better
                  than anticipated). Use this to understand your downside risk and upside potential.
                </p>
                <BarChart title="ROI by Scenario" data={result.sensitivityAnalysis.map((s) => ({
                    label: s.scenario.charAt(0).toUpperCase() + s.scenario.slice(1),
                    value: s.rosi,
                    color: s.scenario === 'optimistic'
                        ? 'bg-green-500'
                        : s.scenario === 'expected'
                            ? 'bg-blue-500'
                            : s.scenario === 'conservative'
                                ? 'bg-orange-500'
                                : 'bg-red-500'
                }))} unit="%" showValues={true}/>
              </CalculatorCard>

              {/* Threat Coverage */}
              {result.threatCoverage && result.threatCoverage.length > 0 && (<CalculatorCard className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-2xl">🛡️</span>
                    Threat Coverage Analysis
                  </h3>
                  <p className="text-gray-600 mb-6">
                    This shows how your security investment reduces the risk level for specific cyber threats.
                    "Before" represents your current vulnerability to each threat, while "After" shows your improved
                    protection level once this investment is deployed. Lower risk levels mean better protection.
                  </p>
                  <div className="space-y-3">
                    {result.threatCoverage.map((tc, index) => (<div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">{tc.threat}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-red-600 font-semibold">
                            Before: {tc.currentRisk}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="text-sm text-green-600 font-semibold">
                            After: {tc.postInvestmentRisk}
                          </span>
                        </div>
                      </div>))}
                  </div>
                </CalculatorCard>)}

              {/* Lead Capture Form */}
              <div className="mb-8">
                <LeadCaptureForm
                  source="cybersecurity-roi-calculator"
                  title={result.recommendation === 'APPROVE' ? 'Ready to Implement This Investment?' : 'Get Expert ROI Optimization Guidance'}
                  description={result.recommendation === 'APPROVE'
                    ? `You've identified a security investment with ${formatPercentage(result.rosiYear1)} Year 1 ROI. Our team can help you select vendors, negotiate pricing, and implement successfully.`
                    : 'The ROI analysis suggests this investment needs refinement. Our team can help you identify alternative solutions or optimize this approach for better returns.'}
                  deliverable="a customized implementation roadmap"
                  resultsSummary={`CYBERSECURITY ROI ANALYSIS RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Investment: ${investmentName}
Recommendation: ${result.recommendation}
Industry: ${industry}
Current Security Maturity: Level ${currentMaturity}

ROI METRICS:
• Year 1 ROI: ${formatPercentage(result.rosiYear1)}
• 5-Year ROI: ${formatPercentage(result.rosi5Year)}
• Payback Period: ${result.paybackPeriod === null ? 'N/A' : result.paybackPeriod < 1 ? `${Math.round(result.paybackPeriod * 12)} months` : `${result.paybackPeriod.toFixed(1)} years`}
• 5-Year NPV: ${formatCurrency(result.npv5Year)}

COSTS:
• Implementation (One-Time): ${formatCurrency(result.costs.oneTime)}
• Annual Operating Cost: ${formatCurrency(result.costs.annual)}
• Year 1 Total Cost: ${formatCurrency(result.costs.year1Total)}

BENEFITS:
• Risk Reduction (Annual): ${formatCurrency(result.benefits.riskReductionAnnual)}
• Additional Benefits: ${formatCurrency(result.benefits.additionalBenefits)}
• Total Year 1 Benefit: ${formatCurrency(result.benefits.totalBenefitYear1)}

RISK PROFILE:
• Estimated Breach Cost: ${formatCurrency(avgBreachCost)}
• Breach Probability: ${breachProbability}%
• Risk Mitigation: ${riskMitigation}%`}
                />
              </div>

              {/* Action Buttons - Bottom */}
              <div className="mt-8">
                {renderActionButtons()}
              </div>
            </>)}
        </>)}

      {showToast && (<Toast message="Link copied! Share this URL to show your ROI calculation." type="success" onClose={() => setShowToast(false)}/>)}
    </div>);
}
